import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import faker from '@faker-js/faker';

import {
  BackgroundImage,
  Flex,
  Text,
  ProgressBar,
  SvgIcon,
  AspectRatio,
  Box,
} from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';
import CourseExpiredModal from 'Routes/Classroom/Components/CourseExpiredModal';
import { getDuration } from 'Util/datetime';
import { AccessContentModal, MESSAGE_TYPE } from 'Components/AccessContentModal';

import { BADGE_VARIANTS, CARD_TYPES, MODAL_TYPE } from './CourseCard';

const CardContainer = styled((props) => (
  <Flex as={'a'} width={1} flexDirection={'column'} {...props} />
))`
  background-color: ${getTheme('colors.white')};
  border-radius: 12px;
  box-shadow: 0 2px 4px 0px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  cursor: pointer;

  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          min-height: 260px;
        `
      : css`
          min-height: 377px;
        `}
`;

const CourseLockedOverlay = styled((props) => (
  <Flex width={1} height={'100%'} justifyContent={'center'} alignItems={'center'} {...props} />
))`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
`;

const BadgeOverlay = styled(Flex)`
  position: absolute;
  display: block;
  height: 100%;
  width: 100%;
`;

const BadgeContainer = styled((props) => (
  <Flex py={1} px={2} justifyContent={'center'} alignItems={'center'} {...props} />
))`
  border-radius: 14px;
  position: absolute;
  right: 16px;
  top: 16px;
  ${({ variant }) =>
    variant === BADGE_VARIANTS.EBOOK
      ? css`
          background-color: ${({ theme }) => theme.colors.white};
        `
      : css`
          background-color: ${({ theme }) => theme.colors.warning};
        `}
`;

const BadgeText = (props) => {
  return <Text textStyle={'body2'} color={'black'} fontWeight={'bold'} {...props} />;
};

const TrialTagContainer = styled((props) => (
  <Flex
    py={'2px'}
    px={2}
    bg={'whites.11'}
    justifyContent={'center'}
    alignItems={'center'}
    {...props}
  />
))`
  border-radius: 14px;
  position: absolute;
  right: 10px;
  bottom: 10px;
`;

const ImageWrapper = styled(Box)`
  width: 100%;
  height: 100%;
`;

const CourseImage = styled(BackgroundImage)`
  padding: 0;
  background-position: top center;
  height: 100%;
  width: 100%;
`;

const CardDetailsContainer = styled((props) => (
  <Flex
    width={1}
    height={'100%'}
    flexDirection={'column'}
    justifyContent={'space-between'}
    flex={1}
    {...props}
  />
))`
  overflow: hidden;

  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          padding: ${getTheme('space.2')}px;
        `
      : css`
          padding: ${getTheme('space.6')}px;
        `}
`;

const CardTitle = styled((props) => <Text mb={2} fontWeight={'bold'} color={'text'} {...props} />)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;

  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          font-size: ${getTheme('fontSizes.1')}px;
          -webkit-line-clamp: 3;
        `
      : css`
          font-size: ${getTheme('fontSizes.2')}px;
          -webkit-line-clamp: 2;
        `}
`;

const Description = styled((props) => <Text mt={2} fontWeight={'normal'} {...props} />)`
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;
  letter-spacing: 0.2px;

  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          font-size: 10px;
        `
      : css`
          font-size: ${getTheme('fontSizes.0')}px;
        `}
`;

const Badge = ({ variant, totalActiveLearningModes }) => {
  switch (variant) {
    case BADGE_VARIANTS.EBOOK:
      return (
        <BadgeOverlay>
          <BadgeContainer variant={variant}>
            <BadgeText>E-Book</BadgeText>
          </BadgeContainer>
        </BadgeOverlay>
      );
    case BADGE_VARIANTS.LEARNING_MODE:
      return (
        <BadgeOverlay>
          <BadgeContainer variant={variant}>
            <BadgeText>{`${totalActiveLearningModes} โหมด`}</BadgeText>
          </BadgeContainer>
        </BadgeOverlay>
      );
    default:
      return null;
  }
};

const TrialTag = ({ variant }) => (
  <BadgeOverlay>
    <TrialTagContainer>
      <Text color={'text'} fontSize={variant === CARD_TYPES.SMALL ? '10px' : 0}>
        ทดลองเรียน
      </Text>
    </TrialTagContainer>
  </BadgeOverlay>
);

const CardDetails = ({ title, timeLeft, expDate, progress, progressLabel, footer, variant }) => {
  return (
    <CardDetailsContainer variant={variant}>
      <CardTitle variant={variant}>{title}</CardTitle>
      <Box>
        <ProgressBar
          value={progress}
          label={progressLabel}
          textProps={{
            fontWeight: 300,
            fontSize: '14px',
          }}
        />
        {(expDate || timeLeft) && (
          <Description mt={3} variant={variant} color={'text'}>
            {expDate ? expDate : null}
            {expDate && timeLeft ? <br /> : null}
            {timeLeft ? timeLeft : null}
          </Description>
        )}
        {footer && (
          <Text textStyle={'caption'} mt={'auto'} color={'greyscale.grey3'}>
            {footer}
          </Text>
        )}
      </Box>
    </CardDetailsContainer>
  );
};

const CourseCardVerticalWithExpiry = ({
  imageUrl,
  defaultImageUrl,
  title,
  time,
  isLockedCourse,
  isShowRemainingTime,
  expDate,
  onClick,
  variant,
  progress,
  footer,
  badgeVariant,
  trialMode,
  containerProps,
  modalType,
  name,
  totalActiveLearningModes,
}) => {
  const { t } = useTranslation('products');
  const [isOpenLockedCourseModal, setIsOpenLockedCourseModal] = useState(false);

  const handleOnClick = () => {
    if (isLockedCourse) {
      setIsOpenLockedCourseModal(true);
    } else {
      return onClick;
    }
  };

  const handleCloseModal = () => {
    setIsOpenLockedCourseModal(false);
  };

  return (
    <Fragment>
      <CardContainer
        data-testid={`${name}-open`}
        variant={variant}
        p={0}
        onClick={isLockedCourse ? handleOnClick : onClick}
        {...containerProps}
      >
        <AspectRatio ratio={16 / 9}>
          <ImageWrapper>
            <CourseImage variant={variant} src={imageUrl || defaultImageUrl} />
            {trialMode && <TrialTag variant={variant} />}
            {isLockedCourse && (
              <CourseLockedOverlay variant={variant}>
                <SvgIcon
                  icon={SvgIcon.ICON_LIST.LOCKED}
                  color={'white'}
                  size={variant === CARD_TYPES.SMALL ? 24 : 42}
                />
              </CourseLockedOverlay>
            )}
            <Badge variant={badgeVariant} totalActiveLearningModes={totalActiveLearningModes} />
          </ImageWrapper>
        </AspectRatio>
        <CardDetails
          variant={variant}
          title={title}
          timeLeft={
            isShowRemainingTime ? t('COURSE_CARD.TIME_LEFT', { timeLeft: getDuration(time) }) : null
          }
          progress={progress}
          progressLabel={t('COURSE_CARD.PROGRESS_SUFFIX')}
          footer={footer}
          expDate={
            expDate
              ? t('COURSE_CARD.EXP_DATE', { expDate: dayjs(expDate).add(-1, 'minute') })
              : isShowRemainingTime
              ? t('COURSE_CARD.NO_EXP_DATE')
              : null
          }
        />
      </CardContainer>

      {modalType === MODAL_TYPE.EXPIRY_MODAL && (
        <CourseExpiredModal isActive={isOpenLockedCourseModal} onContinue={handleCloseModal} />
      )}
      {modalType === MODAL_TYPE.REMAINING_TIMEOUT && (
        <AccessContentModal
          messageType={MESSAGE_TYPE.COURSE_REMAINING_TIMEOUT}
          muiProps={{ isActive: isOpenLockedCourseModal }}
          onClick={handleCloseModal}
        />
      )}
    </Fragment>
  );
};

CourseCardVerticalWithExpiry.propTypes = {
  imageUrl: PropTypes.string,
  defaultImageUrl: PropTypes.string,
  title: PropTypes.string,
  time: PropTypes.number,
  isLockedCourse: PropTypes.bool,
  expDate: PropTypes.string,
  onClick: PropTypes.func,
  progress: PropTypes.number,
  footer: PropTypes.string,
  badgeVariant: PropTypes.string,
  trialMode: PropTypes.bool,
  name: PropTypes.string,
  totalActiveLearningModes: PropTypes.number,
};

CourseCardVerticalWithExpiry.defaultProps = {
  onClick: () => {},
  name: faker.random.uuid(),
};

export default CourseCardVerticalWithExpiry;
